import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { ChevronLeft, ChevronRight, ChevronDown } from 'lucide-react';
import { PayPeriod } from '../interfaces/PayPeriodInterface';
import './WeekPickerNav.css';

export interface WeekPickerNavProps {
  payPeriods: PayPeriod[];
  selectedIdx: number;
  onChange: (idx: number) => void;
}

type PickerView = 'weeks' | 'months' | 'years';

// ── Utilities ────────────────────────────────────────────────────────────────

function isoDateOnly(d: Date): string {
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function addDays(d: Date, n: number): Date {
  const result = new Date(d);
  result.setUTCDate(result.getUTCDate() + n);
  return result;
}

function sameDay(a: Date, b: Date): boolean {
  return isoDateOnly(a) === isoDateOnly(b);
}

function isoWeekNum(d: Date): number {
  const jan4 = new Date(Date.UTC(d.getUTCFullYear(), 0, 4));
  const weekOneStart = addDays(jan4, -(((jan4.getUTCDay() || 7) - 1)));
  return Math.floor((d.getTime() - weekOneStart.getTime()) / (7 * 86400000)) + 1;
}

/** Calendar rows aligned to the tenant's pay-period start day (0=Sun..6=Sat),
 *  so each row's first cell is a candidate pay-period StartDate. NPM runs
 *  Thu→Wed periods; 2020ph Thu→Wed; a Monday-start tenant gets the classic grid. */
function getCalendarRows(year: number, month: number, startDow: number): Date[][] {
  const first = new Date(Date.UTC(year, month, 1));
  const offset = (first.getUTCDay() - startDow + 7) % 7;
  let cur = addDays(first, -offset);
  const last = new Date(Date.UTC(year, month + 1, 0));
  const rows: Date[][] = [];
  for (let i = 0; i < 6; i++) {
    const week: Date[] = Array.from({ length: 7 }, () => {
      const d = new Date(cur);
      cur = addDays(cur, 1);
      return d;
    });
    rows.push(week);
    if (cur.getTime() > last.getTime() && cur.getUTCMonth() !== month) break;
  }
  return rows;
}

const MONTH_NAMES = ['January','February','March','April','May','June',
                     'July','August','September','October','November','December'];
const MONTH_ABBR  = ['Jan','Feb','Mar','Apr','May','Jun',
                     'Jul','Aug','Sep','Oct','Nov','Dec'];

function toUTCDate(d: Date | string): Date {
  const s = typeof d === 'string' ? d : d.toISOString();
  const parts = s.substring(0, 10).split('-').map(Number);
  return new Date(Date.UTC(parts[0], parts[1] - 1, parts[2], 12));
}

function fmtRange(pp: PayPeriod): string {
  const start = toUTCDate(pp.StartDate);
  const end   = toUTCDate(pp.EndDate);
  const sy = start.getUTCFullYear();
  const ey = end.getUTCFullYear();
  const sm = MONTH_ABBR[start.getUTCMonth()];
  const em = MONTH_ABBR[end.getUTCMonth()];
  const sd = start.getUTCDate();
  const ed = end.getUTCDate();
  if (sy !== ey) return `${sm} ${sd}, ${sy} – ${em} ${ed}, ${ey}`;
  if (sm === em)  return `${sm} ${sd} – ${ed}, ${sy}`;
  return `${sm} ${sd} – ${em} ${ed}, ${sy}`;
}

// ── WeekRow ───────────────────────────────────────────────────────────────────

interface WeekRowProps {
  week: Date[];
  weekNum: number;
  isSel: boolean;
  isDisabled: boolean;
  pkMonth: number;
  today: Date;
  onSelect: () => void;
}

function WeekRow({ week, weekNum, isSel, isDisabled, pkMonth, today, onSelect }: WeekRowProps) {
  const [hov, setHov] = useState(false);
  let rowClass = 'week-row';
  if (isSel) rowClass += ' sel';
  else if (hov && !isDisabled) rowClass += ' hov';
  if (isDisabled) rowClass += ' disabled';

  return (
    <div
      className={rowClass}
      onClick={isDisabled ? undefined : onSelect}
      onMouseEnter={() => { if (!isDisabled && !isSel) setHov(true); }}
      onMouseLeave={() => setHov(false)}
    >
      <div className="wnum-pill">W{weekNum}</div>
      {week.map((day, i) => {
        const classes = [
          'cal-day',
          day.getUTCMonth() === pkMonth ? 'in-month' : '',
          sameDay(day, today) ? 'is-today' : '',
        ].filter(Boolean).join(' ');
        return (
          <div key={i} className={classes}>
            {day.getUTCDate()}
          </div>
        );
      })}
    </div>
  );
}

// ── Portalled picker ──────────────────────────────────────────────────────────

interface PickerPortalProps {
  anchorRef: React.RefObject<HTMLDivElement | null>;
  pkView: PickerView;
  pkYear: number;
  pkMonth: number;
  pkDecadeStart: number;
  rows: Date[][];
  today: Date;
  validStartDates: Set<string>;
  selectedISO: string;
  payPeriods: PayPeriod[];
  startDow: number;
  onStep: (dir: number) => void;
  onHeaderClick: () => void;
  onPickYear: (y: number) => void;
  onPickMonth: (m: number) => void;
  onSelect: (idx: number) => void;
  onCurrentWeek: () => void;
  onClose: () => void;
}

function PickerPortal({
  anchorRef, pkView, pkYear, pkMonth, pkDecadeStart,
  rows, today, validStartDates, selectedISO, payPeriods, startDow,
  onStep, onHeaderClick, onPickYear, onPickMonth,
  onSelect, onCurrentWeek, onClose,
}: PickerPortalProps) {
  const pickerRef = useRef<HTMLDivElement>(null);

  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  useLayoutEffect(() => {
    if (anchorRef.current) {
      const r = anchorRef.current.getBoundingClientRect();
      setPos({
        top:  r.bottom + 8,
        left: Math.max(8, r.right - 340),
      });
    }
  }, [anchorRef]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        pickerRef.current && !pickerRef.current.contains(target) &&
        anchorRef.current && !anchorRef.current.contains(target)
      ) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [anchorRef, onClose]);

  if (!pos) return null;

  // Heading text changes per view
  const headingText =
    pkView === 'years'  ? `${pkDecadeStart} – ${pkDecadeStart + 19}` :
    pkView === 'months' ? `${pkYear}` :
    `${MONTH_NAMES[pkMonth]} ${pkYear}`;

  // For highlighting sel year/month
  const selDate  = selectedISO ? new Date(selectedISO + 'T12:00:00Z') : null;
  const selYear  = selDate ? selDate.getUTCFullYear() : -1;
  const selMonth = selDate ? selDate.getUTCMonth()    : -1;

  // Local today for year/month "current" highlights
  const todayYear  = today.getFullYear();
  const todayMonth = today.getMonth();

  return ReactDOM.createPortal(
    <div
      ref={pickerRef}
      className="cal-picker open"
      style={{ position: 'fixed', top: pos.top, left: pos.left, zIndex: 9999 }}
    >
      {/* Header */}
      <div className="pk-head">
        <button className="pk-nav" onClick={() => onStep(-1)}>
          <ChevronLeft size={14} />
        </button>
        <button className="pk-month" onClick={onHeaderClick}>
          {headingText}
        </button>
        <button className="pk-nav" onClick={() => onStep(1)}>
          <ChevronRight size={14} />
        </button>
      </div>

      {/* ── Week view ── */}
      {pkView === 'weeks' && (
        <div className="cal-grid">
          <div className="dow" />
          {Array.from({ length: 7 }, (_, i) => (
            <div key={i} className="dow">{'SMTWTFS'[(startDow + i) % 7]}</div>
          ))}
          {rows.map((week, ri) => {
            const rowStart = week[0]; // aligned to the tenant's period start day
            const rowISO = isoDateOnly(rowStart);
            const isDisabled = !validStartDates.has(rowISO);
            const isSel = rowISO === selectedISO;
            const ppIdx = isDisabled ? -1 : payPeriods.findIndex(pp =>
              isoDateOnly(toUTCDate(pp.StartDate)) === rowISO
            );
            return (
              <WeekRow
                key={ri}
                week={week}
                weekNum={ppIdx >= 0 ? payPeriods[ppIdx].WeekNumber : isoWeekNum(rowStart)}
                isSel={isSel}
                isDisabled={isDisabled}
                pkMonth={pkMonth}
                today={today}
                onSelect={() => { if (ppIdx >= 0) onSelect(ppIdx); }}
              />
            );
          })}
        </div>
      )}

      {/* ── Year grid ── */}
      {pkView === 'years' && (
        <div className="pk-yeargrid">
          {Array.from({ length: 20 }, (_, i) => {
            const y = pkDecadeStart + i;
            const isCurrent = y === todayYear;
            const isSel     = y === selYear;
            return (
              <button
                key={y}
                className={`pk-year-cell${isCurrent ? ' current' : ''}${isSel ? ' sel' : ''}`}
                onClick={() => onPickYear(y)}
              >
                {y}
              </button>
            );
          })}
        </div>
      )}

      {/* ── Month grid ── */}
      {pkView === 'months' && (
        <div className="pk-monthgrid">
          {MONTH_ABBR.map((abbr, mi) => {
            const isCurrent = mi === todayMonth && pkYear === todayYear;
            const isSel     = mi === selMonth && pkYear === selYear;
            return (
              <button
                key={mi}
                className={`pk-month-cell${isCurrent ? ' current' : ''}${isSel ? ' sel' : ''}`}
                onClick={() => onPickMonth(mi)}
              >
                {abbr}
              </button>
            );
          })}
        </div>
      )}

      {/* Footer */}
      <div className="pk-foot">
        <button className="pk-today-btn" onClick={onCurrentWeek}>
          Current Week
        </button>
        <span className="pk-hint-text">
          {pkView === 'weeks' ? 'Click a week to select' :
           pkView === 'months' ? 'Select a month' : 'Select a year'}
        </span>
      </div>
    </div>,
    document.body
  );
}

// ── Main Component ────────────────────────────────────────────────────────────

const WeekPickerNav: React.FC<WeekPickerNavProps> = ({ payPeriods, selectedIdx, onChange }) => {
  const [open, setOpen]               = useState(false);
  const [pkView, setPkView]           = useState<PickerView>('weeks');
  const [pkYear, setPkYear]           = useState(0);
  const [pkMonth, setPkMonth]         = useState(0);
  const [pkDecadeStart, setPkDecadeStart] = useState(0);
  const triggerRef = useRef<HTMLDivElement>(null);

  const selectedPP = payPeriods[selectedIdx];

  // Close on Escape
  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  const handleToggle = () => {
    if (!open && selectedPP) {
      const d = toUTCDate(selectedPP.StartDate);
      setPkYear(d.getUTCFullYear());
      setPkMonth(d.getUTCMonth());
      setPkView('weeks');
    }
    setOpen(o => !o);
  };

  // Step: month / year / decade depending on current view
  const handleStep = (dir: number) => {
    if (pkView === 'years') {
      setPkDecadeStart(s => s + dir * 20);
    } else if (pkView === 'months') {
      setPkYear(y => y + dir);
    } else {
      let m = pkMonth + dir;
      let y = pkYear;
      if (m < 0)  { m = 11; y--; }
      if (m > 11) { m = 0;  y++; }
      setPkMonth(m);
      setPkYear(y);
    }
  };

  // Clicking the month/year heading drills into year grid (or back to weeks)
  const handleHeaderClick = () => {
    if (pkView !== 'years') {
      setPkDecadeStart(pkYear - (pkYear % 20));
      setPkView('years');
    } else {
      setPkView('weeks');
    }
  };

  const handlePickYear = (y: number) => {
    setPkYear(y);
    setPkView('months');
  };

  const handlePickMonth = (m: number) => {
    setPkMonth(m);
    setPkView('weeks');
  };

  // Select the pay period that contains today. Compare calendar dates, not timestamps:
  // toUTCDate anchors every period boundary at 12:00 UTC, so comparing a live `now`
  // against it left a ~24h dead window around each boundary (from noon UTC on a period's
  // last day until noon UTC on the next period's first day) where nothing matched and the
  // button silently did nothing. Local date vs. the period's date-only strings is
  // inclusive at both ends; ISO dates compare correctly as strings.
  const handleCurrentWeek = () => {
    const now = new Date();
    const todayLocalISO = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
    const idx = payPeriods.findIndex(pp =>
      isoDateOnly(toUTCDate(pp.StartDate)) <= todayLocalISO &&
      todayLocalISO <= isoDateOnly(toUTCDate(pp.EndDate))
    );
    if (idx >= 0) onChange(idx);
    // Always close, even with no covering period, so the button never looks inert.
    setOpen(false);
  };

  const validStartDates = new Set(
    payPeriods.map(pp => isoDateOnly(toUTCDate(pp.StartDate)))
  );

  // The tenant's pay-period start day of week (0=Sun..6=Sat), derived from the
  // data — e.g. NPM/2020ph run Thu→Wed. The calendar grid aligns to it so each
  // row's first cell can match a period StartDate (a Monday-locked grid made
  // every row unselectable for non-Monday tenants).
  const refPP = selectedPP ?? payPeriods[0];
  const startDow = refPP ? toUTCDate(refPP.StartDate).getUTCDay() : 1;

  const today = new Date();
  const rows = open && pkView === 'weeks' ? getCalendarRows(pkYear, pkMonth, startDow) : [];
  const selectedISO = selectedPP ? isoDateOnly(toUTCDate(selectedPP.StartDate)) : '';
  const badgeLabel  = selectedPP ? `W${selectedPP.WeekNumber}` : '—';
  const rangeLabel  = selectedPP ? fmtRange(selectedPP) : '—';

  return (
    <div className="weeknav">
      <button
        className="wn-arrow"
        onClick={() => onChange(Math.max(0, selectedIdx - 1))}
        disabled={selectedIdx === 0}
        title="Previous week"
      >
        <ChevronLeft size={15} />
      </button>

      <div className="wn-wrap" ref={triggerRef}>
        <button className={`wn-trigger${open ? ' open' : ''}`} onClick={handleToggle}>
          <span className="wn-badge">{badgeLabel}</span>
          <span className="wn-range">{rangeLabel}</span>
          <ChevronDown size={14} className="wn-caret" />
        </button>

        {open && (
          <PickerPortal
            anchorRef={triggerRef}
            pkView={pkView}
            pkYear={pkYear}
            pkMonth={pkMonth}
            pkDecadeStart={pkDecadeStart}
            rows={rows}
            today={today}
            validStartDates={validStartDates}
            selectedISO={selectedISO}
            payPeriods={payPeriods}
            startDow={startDow}
            onStep={handleStep}
            onHeaderClick={handleHeaderClick}
            onPickYear={handlePickYear}
            onPickMonth={handlePickMonth}
            onSelect={(idx) => { onChange(idx); setOpen(false); }}
            onCurrentWeek={handleCurrentWeek}
            onClose={() => setOpen(false)}
          />
        )}
      </div>

      <button
        className="wn-arrow"
        onClick={() => onChange(Math.min(payPeriods.length - 1, selectedIdx + 1))}
        disabled={selectedIdx >= payPeriods.length - 1}
        title="Next week"
      >
        <ChevronRight size={15} />
      </button>
    </div>
  );
};

export default WeekPickerNav;
